import { useState, useEffect, useCallback } from 'react';
import { usePlatform } from '../../../context/PlatformContext';
import { useAuth } from '../../../context/AuthContext';
import { DEFAULT_THEME_FORM_DATA } from '../../../config/constants';
import { debugError } from '../../../utils/debugLog';

/**
 * Custom hook for Theme Management functionality
 */
export const useThemeManagement = () => {
  const { tenant } = useAuth();
  const {
    themes,
    currentTheme,
    loadTenantThemes,
    createTheme,
    updateTheme,
    deleteTheme,
    activateTheme,
  } = usePlatform();

  // Get tenant ID - ONLY from auth context (no URL parameter access for security)
  const getTenantId = useCallback(() => {
    // Only use the authenticated user's tenant ID - no URL parameter access
    // Try both tenant_id and id properties to handle different tenant object structures
    return tenant?.tenant_id || tenant?.id;
  }, [tenant?.tenant_id, tenant?.id]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentThemeItem, setCurrentThemeItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [previewMode, setPreviewMode] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_THEME_FORM_DATA);

  const fetchThemes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // tenant_id now comes from auth context, not passed as parameter
      await loadTenantThemes(1, 10);
    } catch (err) {
      debugError('useThemeManagement - error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loadTenantThemes]);

  const handleOpenCreateDialog = () => {
    setCurrentThemeItem(null);
    setFormData(DEFAULT_THEME_FORM_DATA);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = theme => {
    setCurrentThemeItem(theme);
    setFormData({
      theme_name: theme.theme_name || '',
      theme_code: theme.theme_code || '',
      description: theme.description || '',
      is_active: theme.is_active || false,
      primary_color: theme.primary_color || '#1976d2',
      secondary_color: theme.secondary_color || '#dc004e',
      background_color: theme.background_color || '#ffffff',
      surface_color: theme.surface_color || '#f8fafc',
      text_primary: theme.text_primary || '#1e293b',
      text_secondary: theme.text_secondary || '#64748b',
      border_color: theme.border_color || '#e2e8f0',
      success_color: theme.success_color || '#10b981',
      warning_color: theme.warning_color || '#f59e0b',
      error_color: theme.error_color || '#ef4444',
      font_family: theme.font_family || 'Inter',
      font_size_base: theme.font_size_base || 16,
      border_radius: theme.border_radius || 8,
      spacing_unit: theme.spacing_unit || 8,
      logo_url: theme.logo_url || '',
      favicon_url: theme.favicon_url || '',
      custom_css: theme.custom_css || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentThemeItem(null);
    setError(null);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleColorChange = (colorName, color) => {
    setFormData(prev => ({
      ...prev,
      [colorName]: color,
    }));
  };

  const handleSubmit = async (themeData = null) => {
    setLoading(true);
    setError(null);
    try {
      const tenantId = getTenantId();
      if (!tenantId) {
        throw new Error(
          'No tenant selected. Please select a tenant to manage themes.'
        );
      }

      const dataToSubmit = themeData || formData;

      if (currentThemeItem) {
        // Update existing theme
        await updateTheme(currentThemeItem.theme_id, dataToSubmit);
      } else {
        // Create new theme (tenant_id comes from auth context)
        await createTheme(dataToSubmit);
      }

      await fetchThemes(); // Refresh list
      handleCloseDialog();
    } catch (err) {
      setError(err.message || 'Failed to save theme');
      throw err; // Re-throw so caller can handle it
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async themeId => {
    if (window.confirm('Are you sure you want to delete this theme?')) {
      setLoading(true);
      setError(null);
      try {
        await deleteTheme(themeId);
        await fetchThemes(); // Refresh list
      } catch (err) {
        setError(err.message || 'Failed to delete theme');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleTheme = async (themeId, isActive) => {
    setLoading(true);
    setError(null);
    try {
      if (isActive) {
        // Activate the theme
        await activateTheme(themeId);
      } else {
        // Deactivate - would need a deactivate endpoint or update status
        await updateTheme(themeId, { status: 'draft', is_active: false });
      }
      await fetchThemes(); // Refresh list
    } catch (err) {
      setError(err.message || 'Failed to toggle theme');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchThemes();
  }, [page, rowsPerPage, fetchThemes]);

  return {
    // State
    loading,
    error,
    themes,
    currentTheme,
    openDialog,
    currentThemeItem,
    page,
    rowsPerPage,
    previewMode,
    formData,

    // Actions
    setPreviewMode,
    setFormData,
    fetchThemes,
    handleOpenCreateDialog,
    handleOpenEditDialog,
    handleCloseDialog,
    handleChange,
    handleColorChange,
    handleSubmit,
    handleDelete,
    handleToggleTheme,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};
